import React, { useState, useRef, useCallback, useEffect } from 'react'
import classNames from 'classnames/bind'
import styles from './DataTable.module.scss'

const cx = classNames.bind(styles)

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DataTableColumn<T> {
  key: string
  label: string
  sortable?: boolean
  width?: string
  render: (row: T) => React.ReactNode
}


export interface DataTableProps<T extends { id: number | string }> {
  columns: DataTableColumn<T>[]
  rows: T[]
  total: number
  page: number
  perPage: number
  lastPage: number
  /** Extra elements rendered in the toolbar left (replaces default search). */
  toolbarLeft?: React.ReactNode
  /** Extra elements rendered in the toolbar right slot */
  toolbarRight?: React.ReactNode
  emptyMessage?: string
  /** Sortable column key currently sorted */
  sortKey?: string
  sortDir?: 'asc' | 'desc'
  onSort?: (key: string, dir: 'asc' | 'desc') => void
  onPageChange?: (page: number) => void
  onPerPageChange?: (perPage: number) => void
  /** Enable row selection checkboxes. Default true. */
  selectable?: boolean
  /** Per-row actions rendered in the dedicated actions column. */
  actions?: (row: T) => React.ReactNode
}


// ── Pagination helpers ────────────────────────────────────────────────────────

function getPages(page: number, lastPage: number): (number | '...')[] {
  if (lastPage <= 7) return Array.from({ length: lastPage }, (_, i) => i + 1)
  if (page <= 4)     return [1, 2, 3, 4, 5, '...', lastPage]
  if (page >= lastPage - 3) {
    return [1, '...', lastPage - 4, lastPage - 3, lastPage - 2, lastPage - 1, lastPage]
  }
  return [1, '...', page - 1, page, page + 1, '...', lastPage]
}

// ── Main component ────────────────────────────────────────────────────────────

function DataTable<T extends { id: number | string }>({
  columns,
  rows,
  total,
  page,
  perPage,
  lastPage,
  /** Extra elements rendered in the toolbar left (replaces default search). */
  toolbarLeft,
  /** Extra elements rendered in the toolbar right slot */
  toolbarRight,
  emptyMessage = 'No records found.',
  sortKey,
  sortDir = 'asc',
  onSort,
  onPageChange,
  onPerPageChange,
  selectable = true,
  actions,
}: DataTableProps<T>) {
  // row selection
  const [selected, setSelected] = useState<Set<number | string>>(new Set())

  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id))
  const someSelected = rows.some(r => selected.has(r.id)) && !allSelected

  const toggleAll = () => {
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev)
        rows.forEach(r => next.delete(r.id))
        return next
      })
    } else {
      setSelected(prev => {
        const next = new Set(prev)
        rows.forEach(r => next.add(r.id))
        return next
      })
    }
  }

  const toggleRow = (id: number | string) => {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // sort
  const handleSort = (key: string) => {
    if (!onSort) return
    if (sortKey === key) {
      onSort(key, sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      onSort(key, 'asc')
    }
  }

  const pages = getPages(page, lastPage)
  const from = total === 0 ? 0 : (page - 1) * perPage + 1
  const to = Math.min(page * perPage, total)

  return (
    <div className={cx('card')}>
      {/* Toolbar */}
      <div className={cx('toolbar')}>
        <div className={cx('toolbarLeft')}>
          {toolbarLeft}
        </div>
        {toolbarRight && (
          <div className={cx('toolbarRight')}>{toolbarRight}</div>
        )}
      </div>

      {/* Table */}
      <div className={cx('tableScroll')}>
        <table className={cx('table')}>
          <thead>
            <tr>
              {selectable && (
                <th className={cx('thCheck')}>
                  <label className={cx('check')}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={el => { if (el) el.indeterminate = someSelected }}
                      onChange={toggleAll}
                    />
                    <span className={cx('box')} />
                  </label>
                </th>
              )}
              {columns.map(col => (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={cx('th', {
                    thSortable: col.sortable,
                    thSortedAsc:  col.sortable && sortKey === col.key && sortDir === 'asc',
                    thSortedDesc: col.sortable && sortKey === col.key && sortDir === 'desc',
                  })}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable && (
                    <span className={cx('sortIco')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </span>
                  )}
                </th>
              ))}
              {actions && <th className={cx('thActions')}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 2 : 1) + (actions ? 1 : 0)} className={cx('emptyCell')}>
                  {emptyMessage}
                </td>
              </tr>
            ) : rows.map(row => (
              <tr key={row.id} className={cx('row', { rowSelected: selectable && selected.has(row.id) })}>
                {selectable && (
                  <td className={cx('tdCheck')}>
                    <label className={cx('check')}>
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleRow(row.id)}
                      />
                      <span className={cx('box')} />
                    </label>
                  </td>
                )}
                {columns.map(col => (
                  <td key={col.key} className={cx('td')}>
                    {col.render(row)}
                  </td>
                ))}
                {actions && (
                  <td className={cx('tdActions')}>
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className={cx('foot')}>
        <div className={cx('footInfo')}>
          <span>
            Showing{' '}
            <strong>{from}–{to}</strong>{' '}
            of{' '}
            <strong>{total.toLocaleString()}</strong>
          </span>
          <select
            className={cx('select')}
            value={perPage}
            onChange={e => onPerPageChange?.(Number(e.target.value))}
          >
            {[15, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n} per page</option>
            ))}
          </select>
        </div>

        {lastPage > 1 && (
          <div className={cx('pager')}>
            <button
              className={cx('pagerBtn')}
              disabled={page === 1}
              onClick={() => onPageChange?.(page - 1)}
              aria-label="Previous"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m15 18-6-6 6-6"/>
              </svg>
            </button>

            {pages.map((p, i) =>
              p === '...' ? (
                <button key={`ellipsis-${i}`} className={cx('pagerBtn')} disabled>…</button>
              ) : (
                <button
                  key={p}
                  className={cx('pagerBtn', { pagerBtnActive: p === page })}
                  onClick={() => onPageChange?.(p as number)}
                >
                  {p}
                </button>
              )
            )}

            <button
              className={cx('pagerBtn')}
              disabled={page === lastPage}
              onClick={() => onPageChange?.(page + 1)}
              aria-label="Next"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default DataTable
